import { useMemo } from "react";
import DOMPurify from "dompurify";
import { useSlugIndex, rewriteHtmlLinks } from "../lib/useLinkPolicy";

/**
 * Renders editor-supplied HTML safely.
 *
 * Preserves a rich set of editorial tags:
 *   block: p, h1-h6, ul, ol, li, blockquote, br, hr
 *   inline: b, strong, i, em, u, s, a, span, small, big, sub, sup, mark, code
 *
 * Strips: script, style, iframe, object, embed, on* handlers, javascript: URIs.
 *
 * Link policy applied to the sanitized HTML string (NOT post-mount DOM):
 *   - Internal `/search?q=X` links matching a published ecosystem entity
 *     are rewritten to `/ecosystem/{kind}/{slug}` (follow, same tab) -
 *     keeps internal PageRank flowing to entity fiches.
 *   - Other external links keep `target="_blank" rel="noopener noreferrer nofollow"`.
 *   - Other internal links are followable in the same tab.
 *
 * The rewrite runs inside `useMemo` against a stable HTML string so the
 * computed result flows through `dangerouslySetInnerHTML` without React
 * undoing it on subsequent re-renders.
 */

const ALLOWED_TAGS = [
  "p", "h1", "h2", "h3", "h4", "h5", "h6",
  "ul", "ol", "li", "blockquote", "br", "hr", "div",
  "b", "strong", "i", "em", "u", "s", "a", "span", "small", "big",
  "sub", "sup", "mark", "code", "pre",
];

const ALLOWED_ATTR = ["href", "target", "rel", "class", "id", "style"];

const ALLOWED_URI_REGEXP = /^(?:(?:https?|mailto|tel):|#|\/|[a-z0-9._~-]+(?:[^a-z+.\-:]|$))/i;


/**
 * API editorial fields can contain plain text, Markdown, or legacy HTML.
 * Convert basic Markdown to HTML before the existing DOMPurify policy.
 * Never trust author-supplied URLs or markup: DOMPurify sanitizes the result.
 */
const escapeHtml = (value) => String(value)
  .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;").replace(/'/g, "&#39;");

const inlineMarkdown = (line) => escapeHtml(line)
  .replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_match, label, url) => `<a href="${url}">${label}</a>`)
  .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
  .replace(/__([^_]+)__/g, "<strong>$1</strong>")
  .replace(/\*([^*\n]+)\*/g, "<em>$1</em>");

export const normalizeEditorialHtml = (value) => {
  if (!value) return "";
  const raw = String(value).replace(/\r\n/g, "\n").trim();
  if (/<\/?(?:p|h[1-6]|ul|ol|li|div|span|strong|em|a|blockquote|br|table)\b/i.test(raw)) return raw;
  const lines = raw.split("\n");
  const output = [];
  let listOpen = false;
  const closeList = () => { if (listOpen) { output.push("</ul>"); listOpen = false; } };
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) { closeList(); continue; }
    const heading = trimmed.match(/^(#{1,6})\s+(.+)$/);
    if (heading) {
      closeList();
      output.push(`<h${heading[1].length}>${inlineMarkdown(heading[2])}</h${heading[1].length}>`);
      continue;
    }
    const bullet = trimmed.match(/^[-*]\s+(.+)$/);
    if (bullet) {
      if (!listOpen) { output.push("<ul>"); listOpen = true; }
      output.push(`<li>${inlineMarkdown(bullet[1])}</li>`);
      continue;
    }
    closeList();
    output.push(`<p>${inlineMarkdown(trimmed)}</p>`);
  }
  closeList();
  return output.join("\n");
};

const HtmlText = ({ html = "", className = "", testid }) => {
  const slugIndex = useSlugIndex();

  const finalHtml = useMemo(() => {
    if (!html) return "";
    const clean = DOMPurify.sanitize(normalizeEditorialHtml(html), {
      ALLOWED_TAGS,
      ALLOWED_ATTR,
      ALLOWED_URI_REGEXP,
      FORBID_TAGS: ["script", "style", "iframe", "object", "embed", "form"],
      FORBID_ATTR: [
        "onerror", "onload", "onclick", "onmouseover", "onmouseout",
        "onfocus", "onblur", "onchange", "onsubmit",
      ],
    });
    return rewriteHtmlLinks(clean, slugIndex);
  }, [html, slugIndex]);

  if (!finalHtml) return null;
  return (
    <div
      data-testid={testid}
      className={`html-text ${className}`}
      dangerouslySetInnerHTML={{ __html: finalHtml }}
    />
  );
};

export default HtmlText;
